import { ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from '@angular/core';

import { filter, switchMap, tap } from 'rxjs/operators';

import {
  PaginationData,
  PaginationRange,
  TiimeButtonModule,
  TiimeIconModule,
  TiimeOverlayModule,
  TiimeOverlayService,
  TiimeTooltipModule
} from 'tiime-material';

import { AuditApiService } from '@api-services/audit-api.service';
import { AuditEntityType } from '@enums/audit-entity-type.enum';
import { Audit } from '@models/audit';
import { StatusType } from '@modules/core/types/status-type.type';
import { HistoryInfosOverlayData } from '@shared-components/history-infos-button/history-infos-overlay/history-infos-overlay-data';
import { HistoryInfosOverlayComponent } from '@shared-components/history-infos-button/history-infos-overlay/history-infos-overlay.component';
import { EntityHistoryDialogData } from '@shared-dialogs/entity-history-dialog/entity-history-dialog-data';
import { EntityHistoryDialogModule } from '@shared-dialogs/entity-history-dialog/entity-history-dialog.module';
import { EntityHistoryDialogService } from '@shared-dialogs/entity-history-dialog/entity-history-dialog.service';

@Component({
  standalone: true,
  selector: 'app-history-infos-button',
  templateUrl: './history-infos-button.component.html',
  imports: [TiimeButtonModule, TiimeIconModule, TiimeTooltipModule, TiimeOverlayModule, EntityHistoryDialogModule],
  styleUrls: ['./history-infos-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HistoryInfosButtonComponent {
  @Input() entityId: number;
  @Input() entityType: AuditEntityType;
  @Input() entityTitle?: string;
  @Input() entitySubtitle?: string;
  @Input() entityProperties: string[];
  @Input() statusType: StatusType = 'declaration';

  @ViewChild('overlayAnchor') overlayAnchor: ElementRef;

  constructor(
    private readonly auditApiService: AuditApiService,
    private readonly entityHistoryDialogService: EntityHistoryDialogService,
    private readonly overlayService: TiimeOverlayService
  ) {}

  openHistoryInfosOverlay(): void {
    this.auditApiService
      .getAudits(this.entityType, this.entityId, PaginationRange.withPageSize(5), this.entityProperties)
      .pipe(
        switchMap((audits: PaginationData<Audit>) => {
          const overlayData: HistoryInfosOverlayData = {
            audits: audits.data,
            title: this.entityTitle,
            subtitle: this.entitySubtitle,
            statusType: this.statusType
          };

          return this.overlayService
            .open(HistoryInfosOverlayComponent, overlayData, {
              disposeOnNavigation: true,
              width: '450px',
              height: 'auto',
              hasBackdrop: true,
              backdropClose: true,
              connectTo: {
                origin: this.overlayAnchor,
                positions: [
                  {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'start',
                    overlayY: 'top',
                    offsetY: 5
                  }
                ]
              }
            })
            .afterClosed()
            .pipe(
              filter((result: boolean) => result),
              tap(() => this.openHistoryDialog())
            );
        })
      )
      .subscribe();
  }

  openHistoryDialog(): void {
    const dialogData: EntityHistoryDialogData = {
      title: this.entityTitle,
      entityId: this.entityId,
      entityType: this.entityType,
      entityProperties: this.entityProperties,
      statusType: this.statusType
    };

    this.entityHistoryDialogService.open({ data: dialogData }).afterClosed().subscribe();
  }
}
